import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import debounce from 'lodash.debounce';
import styled from 'styled-components';

import { LabelType, useProductBrowser } from '@techstyle/react-components';
import { FormattedMessage } from '@techstyle/react-intl';

import { useOptionLabels } from '../../utils/useLabels';
import { usePageNum } from '../GridPageNumProvider/GridPageNumProvider';

import { BOTTOM_SHEET_CONTENT_SIDE_PADDING } from './BottomSheet';

const SortByContainer = styled.div<{ shouldShowDivider: boolean }>`
  ${({ shouldShowDivider, theme }) =>
    shouldShowDivider &&
    `
      padding-bottom: ${theme.spacing.moderate}px;
      border-bottom: 1px solid ${theme.colors.gray250};
  `}
`;

const LABEL_OPTIONS = {
  whiteSpace: 'nowrap',
  labelType: LabelType.SORT_DROPDOWN,
};

const FILTER_FIELD = {
  field: 'sort',
  label: 'Sort',
};

const ContainerLabel = styled.div`
  font-size: 12px;
  font-weight: 400;
  text-transform: uppercase;
  line-height: 1em;
  letter-spacing: 0.11em;
  color: ${props => props.theme.colors.gray500};
  padding-bottom: ${props => props.theme.spacing.moderate}px;
`;

const Pill = styled.button<{
  isActive: boolean;
  isFirst: boolean;
  isLast: boolean;
}>`
  padding: 8px 16px 8px 16px;
  border: 1px solid ${props => props.theme.colors.lavender200};
  border-radius: 8px;
  height: 40px;
  font-size: 14px;
  width: auto;
  white-space: nowrap;
  font-weight: 400;
  scroll-snap-align: center;

  ${({ isActive, theme }) =>
    isActive &&
    `background: ${theme.colors.black900};
    color: ${theme.colors.white}`};
  ${({ isFirst, theme }) => isFirst && `margin-left: ${theme.spacing.small}px`};
  ${({ isLast, theme }) => isLast && `margin-right: ${theme.spacing.small}px`}
`;

const SortByWrapper = styled.div`
  display: flex;
  overflow: auto;
  align-items: flex-start;
  white-space: nowrap;
  height: 40px;
  gap: 8px;
  margin: 0 -${BOTTOM_SHEET_CONTENT_SIDE_PADDING}px;
  scroll-snap-type: x mandatory;

  ::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
`;

type SortValue =
  | 'newest'
  | 'bestsellers'
  | 'pricel2h'
  | 'priceh2l'
  | 'customSort';

type BasicSortOption = {
  value: SortValue;
  sort: SortValue;
  defaultMessage: string;
  isDefault: boolean;
};

type CustomSortOption = BasicSortOption & {
  value: 'customSort';
  sort: Record<string, string>[];
  defaultMessage: string;
  isDefault: true;
};

type Option = BasicSortOption | CustomSortOption;

export interface SortedByDrawerRendererProps {
  selectedOption: Option;
  onChange: (newValue: Option, initial: boolean) => void;
  options: Option[];

  renderOption: (
    filterField: typeof FILTER_FIELD,
    option: Option,
    labelOptions: typeof LABEL_OPTIONS
  ) => JSX.Element;
}

export const SortedByDrawerRenderer: FC<SortedByDrawerRendererProps> = memo(
  ({ selectedOption, onChange, options, renderOption }) => {
    const [currentlySelectedPill, setCurrentlySelectedPill] =
      useState<Option>(selectedOption);

    const optionsRef = useMemo(
      () => options.reduce((acc, { value }) => ({ ...acc, [value]: null }), {}),
      [options]
    );

    const setRef = (key: SortValue, ref: HTMLButtonElement) => {
      optionsRef[key] = ref;
    };

    const debouncedSortHandler = useCallback(debounce(onChange, 300), [
      onChange,
    ]);

    const applyPillSelection = useCallback(
      (option: Option, initial = false) => {
        setCurrentlySelectedPill(option);
        optionsRef[option.value]?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
        debouncedSortHandler(option, initial);
      },
      [optionsRef, debouncedSortHandler]
    );

    const handleOptionChange = (option: Option) => () => {
      if (option.value === currentlySelectedPill.value) {
        return;
      }
      applyPillSelection(option);
    };

    useEffect(() => {
      applyPillSelection(selectedOption, true);
    }, []);
    return (
      <>
        <ContainerLabel>
          <FormattedMessage
            id="site_product_grid.filter_and_sort_title_sorted_by"
            defaultMessage="Sorted By"
          />
        </ContainerLabel>
        <SortByWrapper>
          {options.map((option, index) => {
            const isActive = currentlySelectedPill.value === option.value;
            return (
              <Pill
                isActive={isActive}
                isFirst={index === 0}
                isLast={index === options.length - 1}
                onClick={handleOptionChange(option)}
                key={option.value}
                ref={ref => setRef(option.value, ref)}
              >
                {renderOption(FILTER_FIELD, option, LABEL_OPTIONS)}
              </Pill>
            );
          })}
        </SortByWrapper>
      </>
    );
  }
);

SortedByDrawerRenderer.displayName = 'SortedByDrawerRenderer';

export function SortedByDrawer() {
  const { sort, setSort, sortOptions, totalFilterCount } = useProductBrowser();

  const { renderLabel } = useOptionLabels();
  const { setPageNum } = usePageNum();
  const handleSetSort = useCallback(
    (option, initial) => {
      if (!initial) {
        setPageNum(1);
      }

      setSort(option);
    },
    [setSort, setPageNum]
  );

  const initialSelectedOptionRef = useRef(sort);
  const content = useMemo(
    () => (
      <SortedByDrawerRenderer
        selectedOption={initialSelectedOptionRef.current}
        onChange={handleSetSort}
        options={sortOptions}
        renderOption={renderLabel}
      />
    ),
    [handleSetSort, sortOptions, renderLabel]
  );

  return (
    <SortByContainer shouldShowDivider={totalFilterCount > 0}>
      {content}
    </SortByContainer>
  );
}
